<script lang="ts" setup>
import { ElMessage } from "element-plus";
import {
  formatNumberWithSpaces,
  fiatConvert,
  cryptoConvert,
} from "@/shared/utils";
import { editOffer } from "@/api";
import { OfferPriceType } from "@/shared/enums";

const { t } = useI18n();
const { data: user } = useAuth();
const route = useRoute();
const UseCurrencyStore = useCurrencyStore();
const { getSympolById } = UseCurrencyStore;
const PaymentOptionsStore = usePaymentOptionsStore();
const { getOptionById } = PaymentOptionsStore;

const props = defineProps({
  offers: {
    type: Array,
    required: true,
  },
  showControls: {
    type: Object,
    default: () => ({}),
  },
  type: {
    type: String,
    default: null,
  },
  showUnlimited: {
    type: Boolean,
    default: false,
  },
});

const tableRef = ref<null | any>(null);
const creatingOrderData = ref<null | any>(null);
const showTFAPopup = ref(false);

const createOrder = (data: any) => {
  if (!user.value) {
    ElMessage.warning(t("c-offers-table-loginWarning"));
    return;
  }
  if (!user.value?.tfaEnabled) {
    showTFAPopup.value = true;
    return;
  }
  creatingOrderData.value = data;
};

const toggleOrder = async (offer: any) => {
  try {
    await editOffer({ ...offer });
  } catch ({ data }: any) {
    offer.isActive = !offer.isActive;
    let message = data?.data?.message || t("c-offers-table-toggleError");
    let alert = false;
    switch (data?.data?.code) {
      case 1248:
      case 1249:
        message = t("c-offers-table-insufficientBalance");
        alert = true;
        ElMessageBox({
          type: "warning",
          title: "",
          message: h("p", null, [
            h("span", null, message + " "),
            h(
              "a",
              {
                href: `/user/wallet?currency=${offer.currencyIn}`,
                target: "_blank",
              },
              t("c-offers-table-goToWallet")
            ),
          ]),
        });
        return;
    }
    if (alert) {
      ElMessageBox.alert(message, "", {
        confirmButtonText: "OK",
        type: "warning",
      });
    } else {
      ElMessage.error(message);
    }
  }
};

const emit = defineEmits(["edit", "delete"]);

const onEdit = (offer: any) => {
  emit("edit", offer);
};

const onDelete = (offer: any) => {
  emit("delete", offer);
};

const tableType = computed(() => props.type || route.params.action);
const addOptionModel = ref(false);
const filterBPO = ref<number[]>([]);

const onAddOption = (bpo?: number[]) => {
  freshCreatedOption.value = undefined;
  filterBPO.value = bpo as number[];
  addOptionModel.value = true;
};

const freshCreatedOption = ref<number | undefined>();

const onOptionClose = (optionId?: number) => {
  freshCreatedOption.value = optionId;
  addOptionModel.value = false;
};
const priceDivider = 100;
</script>

<template>
  <div class="offers-table">
    <UserTFAAlert v-model:dialogVisible="showTFAPopup" />
    <ClientOnly>
      <template #fallback>
        <el-skeleton animated />
      </template>
      <el-table
        ref="tableRef"
        class="trade-table"
        stripe
        :data="props.offers"
        style="width: 100%"
      >
        <el-table-column
          prop="offerCreator"
          :label="
            showControls.edit
              ? ''
              : tableType === 'buy'
              ? t('c-offers-table-seller')
              : t('c-offers-table-buyer')
          "
          width="380"
        >
          <template #default="scope">
            <div v-if="showControls.showType" class="mb-1">
              <el-tag v-if="scope.row.type === 'Sell'" type="danger">{{
                t("c-offers-table-sell")
              }}</el-tag>
              <el-tag v-else-if="scope.row.type === 'Buy'" type="success">{{
                t("c-offers-table-buy")
              }}</el-tag>
            </div>
            <OffersTraderInfo :offer-creator="scope.row.offerCreator" />
          </template>
        </el-table-column>
        <el-table-column
          prop="price"
          :label="t('c-offers-table-price')"
          width="180"
          class-name="price-col"
        >
          <template #default="scope">
            <div class="font-size-5 text-nowrap">
              <span class="mobile-label opacity-55"
                >{{ t("c-offers-table-price") }}:</span
              >
              {{
                formatNumberWithSpaces(
                  fiatConvert(
                    scope.row.exchangeRate,
                    true,
                    scope.row.exchangeRateUnit || 100
                  )
                )
              }}
              {{ getSympolById(scope.row.currencyOut) }}
            </div>
            <div v-if="showControls.edit">
              <span class="opacity-55"
                >{{ t("c-offers-editform-priceType") }}:</span
              >
              <br />
              <span
                v-if="scope.row.priceType === OfferPriceType.Percents"
                class="line-height-none inline-block"
              >
                {{ t("c-offers-editform-percentValue") }}
              </span>
              <span
                v-else-if="scope.row.priceType === OfferPriceType.Absolute"
                class="line-height-none inline-block"
              >
                {{ t("c-offers-editform-relativeValue") }}
              </span>
              <span
                v-else-if="scope.row.priceType === OfferPriceType.Custom"
                class="line-height-none inline-block"
              >
                {{ t("c-offers-editform-fixedValue") }}
              </span>
              <br />
              {{ scope.row.priceVal / priceDivider }}
              <span v-if="scope.row.priceType === OfferPriceType.Percents">
                %
              </span>
              <span v-else-if="scope.row.priceType === OfferPriceType.Absolute">
                {{ getSympolById(scope.row.currencyOut) }}
              </span>
              <span v-else-if="scope.row.priceType === OfferPriceType.Custom">
                {{ getSympolById(scope.row.currencyOut) }}
              </span>
            </div>
          </template>
        </el-table-column>
        <el-table-column
          prop="limit"
          :label="t('c-offers-table-availableLimit')"
          :width="280"
        >
          <template #default="scope">
            <div class="font-size-4">
              <span class="mobile-label opacity-55">
                {{ t("c-offers-table-available") }}:
              </span>
              <template v-if="props.showUnlimited && scope.row.isUnlimited">
                <div>
                  {{ scope.row.currencyIn }}
                </div>
                <span class="color-warning">
                  {{ t("c-offers-amount-unlimited") }}
                </span>
              </template>
              <span v-else>
                {{
                  cryptoConvert(
                    scope.row.offerAmount,
                    scope.row.currencyIn,
                    true
                  )
                }}
                {{ scope.row.currencyIn }}
              </span>
            </div>
            <div class="font-size-4">
              <span class="mobile-label opacity-55"
                >{{ t("c-offers-table-offerLimits") }}:</span
              >
              {{
                formatNumberWithSpaces(fiatConvert(scope.row.minLimit, true))
              }}
              -
              {{
                formatNumberWithSpaces(fiatConvert(scope.row.maxLimit, true))
              }}
              {{ getSympolById(scope.row.currencyOut) }}
            </div>
          </template>
        </el-table-column>
        <el-table-column
          prop="paymentOptions"
          :label="t('c-offers-table-paymentOptions')"
        >
          <template #default="scope">
            <el-row :gutter="20" class="button-row">
              <el-col :span="-1" class="payment-options-col">
                <span class="mobile-label opacity-55"
                  >{{ t("c-offers-table-paymentOptions") }}:</span
                >
                <p
                  v-for="option in scope.row.basePaymentOptions.length > 3
                    ? scope.row.basePaymentOptions.slice(0, 2)
                    : scope.row.basePaymentOptions"
                  class="text-wrap"
                >
                  {{ getOptionById(option)?.name }}
                </p>
                <el-tooltip
                  v-if="scope.row.basePaymentOptions.length > 3"
                  class="box-item"
                  effect="dark"
                  placement="bottom-start"
                >
                  <template #content>
                    <p
                      v-for="item in scope.row.basePaymentOptions.slice(2).map((option: any) => getOptionById(option)?.name)"
                    >
                      {{ item }}
                    </p>
                  </template>
                  <div class="color-primary cursor-pointer">
                    + {{ scope.row.basePaymentOptions.length - 2 }}
                    {{ t("c-offers-table-more") }}...
                  </div>
                </el-tooltip>
              </el-col>
              <div class="flex-grow"></div>
              <el-col :span="-1" class="button-cont">
                <div class="mb-2" v-if="showControls.active">
                  <el-switch
                    v-model="scope.row.isActive"
                    @change="toggleOrder(scope.row)"
                    inline-prompt
                    style="--el-switch-on-color: #13aa66"
                    :active-text="t('c-offers-table-active')"
                    :inactive-text="t('c-offers-table-disabled')"
                  ></el-switch>
                </div>
                <el-button
        round
                  v-if="showControls.edit"
                  @click="onEdit(scope.row)"
                  size="small"
                >
                  {{ t("c-offers-table-edit") }}
                </el-button>
                <el-button
        round
                  v-if="showControls.delete"
                  @click="onDelete(scope.row)"
                  type="danger"
                  size="small"
                  plain
                >
                  {{ t("c-offers-table-delete") }}
                </el-button>
                <el-button
        round
                  v-if="showControls.createOrder"
                  class="buy-button"
                  :type="tableType === 'buy' ? 'success' : 'danger'"
                  @click="createOrder(scope.row)"
                  size="large"
                >
                  {{
                    tableType === "buy"
                      ? t("c-offers-table-buy")
                      : t("c-offers-table-sell")
                  }}
                  {{ scope.row.currencyIn }}
                </el-button>
              </el-col>
            </el-row>
          </template>
        </el-table-column>
      </el-table>
    </ClientOnly>
    <OrderCreate
      v-if="creatingOrderData"
      :order-data="creatingOrderData"
      :pre-selected-option="freshCreatedOption"
      @close="creatingOrderData = null"
      @addOption="onAddOption"
    />
    <ClientOnly>
      <PaymentOptionCreate
        v-if="addOptionModel"
        @close="onOptionClose"
        :filterBPO="filterBPO"
      />
    </ClientOnly>
  </div>
</template>

<style scoped lang="scss">
.offers-table {
  .mobile-label {
    display: none;
  }
  @media (max-width: 1100px) {
    :deep(table) {
      display: block;
      width: 100% !important;
      tbody,
      thead {
        width: 100%;
        display: block;
      }
      tr {
        display: flex;
        float: none;
        width: 100%;
        border-bottom: var(--el-table-border);
        &::after {
          content: "";
          display: block;
          float: none;
          clear: both;
        }
        td,
        th {
          display: block;
          float: left;
          width: 25%;
          border: none !important;
        }

        @media (max-width: 800px) {
          flex-direction: column;
          td,
          th {
            width: 100%;
          }
          th {
            display: none;
          }
          &:hover {
            td,
            th {
              background: none !important;
            }
          }
          .buy-button {
            width: 100%;
          }
          .button-row {
            display: flex;
            justify-content: center;
            flex-direction: column;
          }
          .mobile-label {
            display: inline-block;
          }
          .el-button {
            margin: 0 0.5rem 0.5rem 0;
          }
          .payment-options-col {
            .mobile-label,
            p {
              margin-right: 0.2rem;
            }
            p {
              display: inline-block;

              &::after {
                content: ",";
              }
              &:last-child::after {
                content: none;
              }
            }
          }
        }

        @media (max-width: 400px) {
          .button-cont {
            display: flex;
            justify-content: center;
            flex-direction: column;
          }
        }
      }
    }
  }
}
</style>
